import './special_pay.css'

import {
  CalendarFilled,
  CalendarOutlined,
  FileImageOutlined,
  FontSizeOutlined,
  NumberOutlined,
} from '@ant-design/icons'
import { usePayrollConfig } from '@api/usePayrollConfig'
import { ManyTag } from '@components/_SiteWide/CellTemplates/ManyTag'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { BBDTag, GraviGrid, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { titleCase, triggerGridRead } from '@utils/general'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'
import { RatebookCellRenderer } from './RatebookCellRenderer'

const STORAGE_KEY = 'special-payroll-config'

const typeIconMap = {
  text: <FontSizeOutlined />,
  number: <NumberOutlined />,
  date: <CalendarOutlined />,
  date_range: <CalendarFilled />,
  photo: <FileImageOutlined />,
}

function YesNoRenderer({ value }) {
  return value ? <BBDTag theme2>Yes</BBDTag> : <BBDTag>No</BBDTag>
}

export function SpecialPayConfigs() {
  const [filters, setFilters] = useState()
  const { useSpecialPayConfigQuery, useUploadSpecialPayConfigs } = usePayrollConfig()
  const { data: specialPayConfigs, isFetching } = useSpecialPayConfigQuery(filters)
  const { mutateAsync: uploadSpecialPayConfig } = useUploadSpecialPayConfigs('specialPayConfig')
  const gridReference = useRef()
  const triggerRefresh = () => triggerGridRead(gridReference)
  const { updateSetting } = useUserSettings()
  const columnDefs = useMemo(() => getColumnDefs(), [])
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
    }),
    []
  )
  return (
    <GraviGrid
      controlBarProps={{
        title: 'Special Pay Items',
        actionButtons: (
          <UploadDownloadButtons
            downloadEP={api.downloadSpecialPayConfig}
            uploadEP={uploadSpecialPayConfig}
            fileName='Special Pay Configs.xlsx'
            refreshCallback={triggerRefresh}
          />
        ),
        filters,
        setFilters,
      }}
      loading={isFetching}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      rowGroupPanelShow='never'
      storageKey={STORAGE_KEY}
      rowData={specialPayConfigs?.rows}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = () => [
  {
    field: 'name',
    cellRenderer: ({ value }) => <b>{value}</b>,
    minWidth: 300,
  },
  {
    field: 'tags',
    cellRenderer: ({ value }) => <ManyTag tagItems={value?.map(titleCase)} maxCount={10} />,
  },
  {
    field: 'unit',
  },
  {
    field: 'rate',
    type: 'rightAligned',
  },
  {
    field: 'rate_type',
    headerName: 'Rate Type',
  },
  {
    field: 'calculation_text',
    headerName: 'Calculation Text',
    cellRenderer: ({ value }) => {
      return value === '' ? '' : <div className='calculation-text'>{value}</div>
    },
  },
  {
    field: 'overwrite_auto_pay',
    headerName: 'Overwrite Auto Pay',
    cellRenderer: YesNoRenderer,
  },

  {
    field: 'require_order_number',
    headerName: 'Require Order Number',
    cellRenderer: YesNoRenderer,
  },
  {
    field: 'display_on_ticket',
    headerName: 'Display on Ticket',
    headerTooltip: 'Determines if this item will display on the email customer ticket',
    cellRenderer: YesNoRenderer,
  },
  {
    field: 'require_approval_to_display_on_ticket',
    headerName: 'Require Approval to Display on Ticket',
    headerTooltip:
      'Determines if an “Accessorial Events” section with “Accessorial Event(s) - Pending Approval” text will display on the ticket and then resend an updated ticket with the approved items.',
    cellRenderer: YesNoRenderer,
  },
  {
    field: 'customer_ratebook',
    headerName: 'Customer Ratebook',
    cellRenderer: RatebookCellRenderer,
    autoHeight: true,
  },
  {
    field: 'carrier_ratebook',
    headerName: 'Carrier Ratebook',
    cellRenderer: RatebookCellRenderer,
    autoHeight: true,
  },
  {
    field: 'extra_fields',
    headerName: 'Extra Fields',
    width: '20em',
    cellRenderer: ({ value }) => {
      const tagItems = value?.map((payItem, index) => {
        if (index < 3) {
          return (
            <Horizontal alignItems='center' justifyContent='space-between'>
              <Vertical className='pr-2'>{typeIconMap[payItem.type]}</Vertical>
              <Vertical>{payItem.name}</Vertical>
            </Horizontal>
          )
        }
        return payItem.name
      })

      return <ManyTag tagItems={tagItems} maxCount={3} />
    },
  },
]
